import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { Role } from '@/types'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true } },
    { path: '/auth/callback', name: 'auth-callback', component: () => import('@/views/AuthCallback.vue'), meta: { public: true } },
    { path: '/pending', name: 'pending-access', component: () => import('@/views/PendingAccessView.vue'), meta: { public: true } },
    { path: '/', name: 'landing', component: () => import('@/views/LandingView.vue'), meta: { public: true } },
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      children: [
        { path: 'home', redirect: '/apps' },
        { path: 'apps', name: 'apps', component: () => import('@/views/AppsView.vue') },

        { path: 'apps/:clientId', name: 'my-app-detail', component: () => import('@/views/MyAppDetailView.vue') },
        { path: 'change-requests', name: 'change-requests', component: () => import('@/views/ChangeRequestsView.vue') },
        { path: 'change-requests/:id', name: 'change-request-detail', component: () => import('@/views/ChangeRequestDetailView.vue') },
        { path: 'profile', name: 'profile', component: () => import('@/views/ProfileView.vue') },
        { path: 'developer', name: 'developer', component: () => import('@/views/DeveloperView.vue') },
        { path: 'docs/:slug?', name: 'docs', component: () => import('@/views/DocsView.vue') },
        { path: 'gallery', name: 'gallery', component: () => import('@/views/GalleryView.vue') },

        // ── Admin routes (role-gated) ────────────────────────────────────
        // Legacy redirects (Monitor/Operations merged into Releases)
        { path: 'admin/monitor', redirect: '/admin/releases' },
        { path: 'dashboard', redirect: '/admin/releases' },

        // Partners
        { path: 'admin/partners', name: 'partners', component: () => import('@/views/PartnersView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/partners/registry', redirect: to => ({ path: '/admin/partners', query: { source: 'registry' } }) },
        { path: 'admin/partners/new', name: 'partners-new', component: () => import('@/views/PartnerNewView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/partners/:id', name: 'partner-detail', component: () => import('@/views/PartnerDetailView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/partners/:id/apps', name: 'partner-apps', component: () => import('@/views/PartnerAppsView.vue'), meta: { role: 'dexcom_management' } },
        // Redirect old portal app detail route to unified view
        { path: 'admin/partners/:id/apps/:appId', redirect: to => `/admin/app/${to.params.appId}` },
        { path: 'admin/partners/:id/apps/:appId/settings', redirect: to => `/admin/app/${to.params.appId}/settings` },
        { path: 'admin/partners/:id/settings', name: 'partner-settings', component: () => import('@/views/PartnerSettingsView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/partners/:id/members', name: 'partner-members', component: () => import('@/views/PartnerMembersView.vue'), meta: { role: 'dexcom_management' } },

        // Unified app detail view (merges Portal + Registry detail views)
        { path: 'admin/app/:clientId', name: 'app-detail', component: () => import('@/views/AppDetailView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/app/:clientId/settings', name: 'app-settings', component: () => import('@/views/AppSettingsView.vue'), meta: { role: 'dexcom_management' } },

        // Releases
        { path: 'admin/releases', name: 'releases', component: () => import('@/views/ReleasesView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/releases/new', name: 'releases-new', component: () => import('@/views/ReleaseBuilderView.vue'), meta: { role: 'dexcom_management' } },
        { path: 'admin/releases/:id', name: 'release-detail', component: () => import('@/views/ReleaseDetailView.vue'), meta: { role: 'dexcom_management' } },

        // Provisioner catalog admin — dexcom_admin only, the server enforces too.
        { path: 'admin/provisioner/envs', name: 'provisioner-envs', component: () => import('@/views/ProvisionerEnvsView.vue'), meta: { role: 'dexcom_admin' } },
        { path: 'admin/provisioner/clients', name: 'provisioner-clients', component: () => import('@/views/ProvisionerClientsView.vue'), meta: { role: 'dexcom_admin' } },
        { path: 'admin/provisioner/settings', name: 'provisioner-settings', component: () => import('@/views/ProvisionerSettingsView.vue'), meta: { role: 'dexcom_admin' } },
        { path: 'admin/provisioner/source-streams', name: 'provisioner-source-streams', component: () => import('@/views/SourceStreamsView.vue'), meta: { role: 'dexcom_admin' } },
        // Live partners view — read access for dexcom_management+; mutations (Refresh GCP, Add Client) gated server-side.
        { path: 'admin/provisioner/partners', name: 'provisioner-partners', component: () => import('@/views/ProvisionerPartnersView.vue'), meta: { role: 'dexcom_management' } },

        // Gallery (admin variant with management features)
        { path: 'admin/gallery', name: 'admin-gallery', component: () => import('@/views/GalleryView.vue'), meta: { role: 'dexcom_management' } },

        // AI assistant
        { path: 'admin/ai', name: 'ai', component: () => import('@/views/AiView.vue'), meta: { role: 'dexcom_management' } },

        // Audit logs (director+)
        { path: 'admin/audit-logs', name: 'audit-logs', component: () => import('@/views/AuditLogsView.vue'), meta: { role: 'dexcom_director' } },

        // System settings (admin only)
        { path: 'admin/settings', name: 'settings', component: () => import('@/views/SettingsView.vue'), meta: { role: 'dexcom_admin' } },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/apps' },
  ],
})

// Handle chunk load failures during deployments (stale index.html referencing old chunk hashes).
// Force a full page reload so the browser fetches the new index.html with correct chunk references.
router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module')
  ) {
    // Prevent infinite reload loops — only reload once per path
    const reloadKey = `chunk-reload:${to.fullPath}`
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1')
      window.location.assign(to.fullPath)
    }
  }
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (to.meta.public) return true

  if (!auth.token) return '/login'

  if (!auth.user) {
    await auth.fetchUser()
    if (!auth.user) return '/login'
  }

  // Pending users can only see the pending page
  if (auth.user?.status === 'pending') {
    return to.name === 'pending-access' ? true : '/pending'
  }

  if (to.meta.role && !auth.hasRole(to.meta.role as Role)) {
    return '/apps'
  }

  return true
})

export default router
